// Pure helpers for the template composer matching Meta Cloud API specifications.

import type { TplButton } from './templateTypes';

/** Meta template name pattern: lowercase letters, digits, underscores, up to 512 chars. */
const TEMPLATE_NAME_RE = /^[a-z0-9_]{1,512}$/;

export function isValidTemplateName(name: string): boolean {
  return TEMPLATE_NAME_RE.test(name.trim());
}

/** Extracts all unique variable indices from text, sorted numerically (e.g. {{1}}, {{2}} -> [1, 2]). */
export function extractVariables(text: string): number[] {
  if (!text) return [];
  const matches = Array.from(text.matchAll(/\{\{(\d+)\}\}/g));
  const set = new Set<number>();
  for (const m of matches) {
    const num = parseInt(m[1], 10);
    if (!Number.isNaN(num) && num > 0) {
      set.add(num);
    }
  }
  return Array.from(set).sort((a, b) => a - b);
}

/** Highest {{N}} index used in text. */
export function countTemplateVariables(text: string): number {
  const vars = extractVariables(text);
  return vars.length > 0 ? vars[vars.length - 1] : 0;
}

export interface BodyPart {
  text: string;
  variable: boolean;
  varIndex?: number;
}

/** Split a body into plain text and {{N}} variable segments for preview. */
export function splitTemplateBody(body: string): BodyPart[] {
  if (!body) return [];
  const parts: BodyPart[] = [];
  const re = /\{\{(\d+)\}\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) {
      parts.push({ text: body.slice(last, m.index), variable: false });
    }
    const idx = parseInt(m[1], 10);
    parts.push({ text: m[0], variable: true, varIndex: idx });
    last = m.index + m[0].length;
  }
  if (last < body.length) {
    parts.push({ text: body.slice(last), variable: false });
  }
  return parts;
}

/** Substitute {{n}} placeholders with example values (falling back to placeholder if missing). */
export function replaceVariablesWithExamples(
  text: string,
  variables: Array<{ index: number; example: string }>,
): string {
  if (!text) return '';
  const map = new Map<number, string>();
  for (const v of variables) {
    if (v.example && v.example.trim().length > 0) {
      map.set(v.index, v.example);
    }
  }

  return text.replace(/\{\{(\d+)\}\}/g, (match, p1) => {
    const idx = parseInt(p1, 10);
    return map.has(idx) ? map.get(idx)! : match;
  });
}

/**
 * Validates mixed-button ordering rule per Meta specification:
 * Quick reply buttons cannot be mixed or interleaved with other button types.
 * All quick replies must be grouped consecutively (e.g. all at start or all at end).
 */
export function validateMixedButtonsOrder(buttons: TplButton[]): { valid: boolean; error?: string } {
  if (!buttons || buttons.length <= 1) {
    return { valid: true };
  }

  if (buttons.length > 10) {
    return { valid: false, error: 'Maximum 10 buttons allowed' };
  }

  let urlCount = 0;
  let phoneCount = 0;
  let copyCodeCount = 0;
  let quickReplyCount = 0;

  for (const b of buttons) {
    if (b.type === 'URL') urlCount++;
    else if (b.type === 'PHONE_NUMBER') phoneCount++;
    else if (b.type === 'COPY_CODE') copyCodeCount++;
    else if (b.type === 'QUICK_REPLY') quickReplyCount++;
  }

  if (urlCount > 2) {
    return { valid: false, error: 'Maximum 2 Visit Website (URL) buttons allowed' };
  }
  if (phoneCount > 1) {
    return { valid: false, error: 'Maximum 1 Call Phone Number button allowed' };
  }
  if (copyCodeCount > 1) {
    return { valid: false, error: 'Maximum 1 Copy Offer Code button allowed' };
  }

  // Check if mixed (has quick reply AND other buttons)
  const otherCount = urlCount + phoneCount + copyCodeCount;
  if (quickReplyCount > 0 && otherCount > 0) {
    if (quickReplyCount > 3) {
      return { valid: false, error: 'Maximum 3 Quick Reply buttons allowed in mixed templates' };
    }

    // Check contiguous grouping of quick replies
    const qrIndices: number[] = [];
    buttons.forEach((b, idx) => {
      if (b.type === 'QUICK_REPLY') {
        qrIndices.push(idx);
      }
    });

    for (let i = 1; i < qrIndices.length; i++) {
      if (qrIndices[i] !== qrIndices[i - 1] + 1) {
        return {
          valid: false,
          error: 'Quick reply buttons must be grouped consecutively without other buttons in between',
        };
      }
    }
  }

  return { valid: true };
}

/** Automatically re-orders buttons to satisfy Meta's contiguous grouping requirement (Quick replies at end). */
export function groupButtonsForMeta(buttons: TplButton[]): TplButton[] {
  const others = buttons.filter((b) => b.type !== 'QUICK_REPLY');
  const quickReplies = buttons.filter((b) => b.type === 'QUICK_REPLY');
  return [...others, ...quickReplies];
}
